import React, {useImperativeHandle, useMemo, useRef, useState} from 'react'
import WizModal from '../../../components/modal/WizModal'
import useQueryGetUserPointHistory from '../../../hooks/api/point/useQueryGetUserPointHistory'
import Text from '../../../components/text/Text'
import {IUserPointHistory} from '../../../apis/point/getUserPointHistory'
import {useTime} from '../../../utils/time'
import WizSelect from '../../../components/select/WizSelect'
import WizButton from '../../../components/button/WizButton'
import {getListFromKeyword} from '../../../components/select/constSelect'
import {editUserPointRetract} from '../../../apis/point/editUserPointRetract'
import useQueryUserDetail from '../../../hooks/api/user/useQueryUserDetail'

interface IProps {
    className?: string
    userId?: string
}

const UserPointHistoryModal = ({className, userId}: IProps, ref: any) => {
    const modalRef = useRef<any>()
    const {data: pointHistory} = useQueryGetUserPointHistory(userId)
    const {refetch} = useQueryUserDetail(userId)
    const [revertData, setRevertData] = useState<IUserPointHistory>()
    const [reason, setReason] = useState<any>(1)
    const [otherReason, setOtherReason] = useState<string>('')
    const {convertUtc0ToUtc9} = useTime()

    const groupedHistory = useMemo(() => {
        const groups = pointHistory?.reduce(
            (ob, item) => ({
                ...ob,
                [item?.created_at?.split('T')[0]]: [...(ob[item?.created_at?.split('T')[0]] ?? []), item],
            }),
            {},
        )
        return Object?.keys(groups ?? {}).map(date => ({
            date,
            data: groups[date],
        }))
    }, [pointHistory])

    useImperativeHandle(ref, () => ({
        show: () => {
            modalRef.current.show()
        },
    }))

    const onRevert = async () => {
        const revertReason = getListFromKeyword['revert_point']?.find(item => item?.id === reason)
        const getText = () => {
            if (revertReason?.id === 'others') {
                return otherReason
            } else {
                return revertReason?.value
            }
        }

        const {data, error} = await editUserPointRetract({
            tx_id: revertData?.tx_id,
            reason: `${getText()} - ${revertData?.name}`,
        })

        if (data?.success) {
            alert(`[Success] 포인트가 회수되었습니다. \n\n 회수된 포인트 : ${revertData?.amount}LP`)
            await refetch()
        } else {
            alert('[Error] 포인트 회수에 실패했습니다. 어드민 관리자에게 문의 부탁드립니다.')
        }
    }

    return (
        <>
            <WizModal
                onClose={() => {
                    setRevertData(null)
                }}
                title={'Point History'}
                className={`${className} max-h-[80vh] !w-auto`}
                ref={modalRef}>
                <div className={'flex gap-[30px]'}>
                    <article
                        className={
                            'overflow-auto max-h-[500px] sm:h-full space-y-[30px] px-[10px] sm:px-[20px] mt-[30px] min-w-[420px]'
                        }>
                        {groupedHistory?.map((e, idx) => {
                            return (
                                <div
                                    key={idx}
                                    className={`grid grid-cols-[100px_auto] sm:grid-cols-[80px_auto] gap-x-[20px] border-b border-b-line03 pb-[30px]`}>
                                    <div>
                                        <Text className={'text-body2 text-font03 sm:text-body3'}>{e?.date}</Text>
                                    </div>
                                    <div className={'space-y-[10px]'}>
                                        {e?.data?.map((e, idx) => {
                                            return (
                                                <div key={idx} className={'w-full flex flex-col'}>
                                                    <div className={'flex items-center gap-[10px]'}>
                                                        <Text className={'text-ti2 text-font1 flex-1'}>{e?.name}</Text>
                                                        <Text
                                                            className={`text-ti2 text-[15px] ${
                                                                e?.amount > 0 ? 'text-font_green' : 'text-font01 '
                                                            }`}>
                                                            {e?.amount}LP
                                                        </Text>
                                                        <div className={'w-[1px] bg-line01 h-[12px]'} />
                                                        <Text
                                                            onClick={() => {
                                                                setRevertData(e)
                                                            }}
                                                            className={
                                                                'text-font_red text-ti2 cursor-pointer hover:bg-font_red hover:bg-opacity-[0.1] py-[1px] px-[3px] rounded-[3px]'
                                                            }>
                                                            Retract
                                                        </Text>
                                                    </div>
                                                    <Text className={'text-body3 text-font03'}>
                                                        {e?.description || ' '}
                                                    </Text>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )
                        })}
                    </article>
                    {revertData && (
                        <>
                            <div className={'w-[1px] mt-[30px] h-auto bg-line02'} />
                            <div className={'min-w-[300px] h-fit'}>
                                <article className={'fixed top-[75px] right-[30px]'}>
                                    <Text className={'text-h2 relative top-[-55px] text-center'}>Retract</Text>
                                    <div
                                        className={
                                            'min-w-[300px] p-[20px] rounded-[5px] border border-line03 space-y-[30px]'
                                        }>
                                        <div>
                                            <Text className={'text-ti2 text-font02 text-center'}>Content</Text>
                                            <Text className={'text-ti1 text-center'}>{revertData?.name}</Text>
                                        </div>
                                        <div>
                                            <Text className={'text-ti2 text-font02 text-center'}>Amount</Text>
                                            <Text className={'text-ti1 text-font_red text-center'}>
                                                -{revertData?.amount} LP
                                            </Text>
                                        </div>
                                        <div>
                                            <Text className={'text-ti2 text-font02 text-center'}>Point Income At</Text>
                                            <Text className={'text-ti1 text-center'}>
                                                {convertUtc0ToUtc9(revertData?.created_at)}
                                            </Text>
                                        </div>
                                        <div>
                                            <Text className={'text-ti2 text-font02'}>Reason</Text>
                                            <WizSelect
                                                buttonClassName={'w-full'}
                                                optionsClassName={'w-full'}
                                                optionKey="revert_point"
                                                onChange={e => setReason(e)}
                                                value={reason}
                                            />
                                            {reason === 'others' && (
                                                <input
                                                    className={
                                                        'border-b border-b-line01 w-full p-[5px] text-body1 mt-[10px]'
                                                    }
                                                    onChange={e => {
                                                        setOtherReason(e?.target?.value)
                                                    }}
                                                    placeholder={'Reason'}
                                                />
                                            )}
                                        </div>
                                        <WizButton
                                            text={'Retract'}
                                            onClick={onRevert}
                                            className={'w-full'}
                                            size={'big'}
                                            type={'red'}
                                        />
                                    </div>
                                </article>
                            </div>
                        </>
                    )}
                </div>
            </WizModal>
        </>
    )
}

export default React.forwardRef(UserPointHistoryModal)
